/**
 * Runtime settings for the variants you can play against the bot. Small on purpose:
 * the play page imports it, while the long-form history lives in src/content/variants.ts.
 */
export type PlayableVariantId = "chess960" | "crazyhouse" | "atomic" | "threecheck" | "kingofthehill" | "antichess" | "horde" | "racingkings";

export type PlayableVariant = {
  id: PlayableVariantId;
  /** The matching page in src/content/variants.ts. */
  slug: string;
  name: string;
  /** Fairy-Stockfish (ffish) variant name. */
  rulesId: string;
  /** Which bot plays it: the multi-variant Stockfish, or a small search over ffish's own move generator. */
  engine: { kind: "stockfish"; uciVariant: string } | { kind: "search" };
  chess960?: boolean;
  /** The objective in one line, shown during the game. */
  goal: string;
  /** Short reminders shown beside the board. */
  reminders: string[];
  /** Pieces captured go to a pocket and can be dropped back. */
  pockets?: boolean;
  /** Squares tinted on the board for the whole game. */
  zone?: { squares: string[]; label: string };
};

const HILL = ["d4", "e4", "d5", "e5"];
const FINISH = ["a8", "b8", "c8", "d8", "e8", "f8", "g8", "h8"];

export const PLAYABLE_VARIANTS: PlayableVariant[] = [
  {
    id: "chess960", slug: "chess960", name: "Chess960", rulesId: "chess", chess960: true,
    engine: { kind: "stockfish", uciVariant: "chess" },
    goal: "Checkmate the king, exactly as in standard chess.",
    reminders: ["The back rank is shuffled, so opening theory won’t help.", "To castle, move your king onto the rook it castles with.", "After castling, king and rook land on the usual squares: g1 and f1, or c1 and d1."],
  },
  {
    id: "crazyhouse", slug: "crazyhouse", name: "Crazyhouse", rulesId: "crazyhouse", pockets: true,
    engine: { kind: "stockfish", uciVariant: "crazyhouse" },
    goal: "Checkmate the king. Pieces you capture join your army.",
    reminders: ["Choose a piece in your pocket, then an empty square to drop it.", "Pawns can’t be dropped on the first or last rank.", "A promoted piece goes back to being a pawn when it is captured."],
  },
  {
    id: "atomic", slug: "atomic-chess", name: "Atomic", rulesId: "atomic",
    engine: { kind: "stockfish", uciVariant: "atomic" },
    goal: "Blow up the enemy king, or checkmate it.",
    reminders: ["Every capture explodes: both pieces and every non-pawn piece next to the square are removed.", "Kings can’t capture.", "Kings standing side by side can’t be checked."],
  },
  {
    id: "threecheck", slug: "three-check-chess", name: "Three-check", rulesId: "3check",
    engine: { kind: "stockfish", uciVariant: "3check" },
    goal: "Give check three times, or checkmate.",
    reminders: ["Every check counts, even a harmless one.", "Keep your king covered: open lines become checks."],
  },
  {
    id: "kingofthehill", slug: "king-of-the-hill", name: "King of the Hill", rulesId: "kingofthehill", zone: { squares: HILL, label: "The hill" },
    engine: { kind: "stockfish", uciVariant: "kingofthehill" },
    goal: "Bring your king safely to one of the four centre squares, or checkmate.",
    reminders: ["The hill is d4, e4, d5 and e5.", "Your king can only step onto the hill with a legal move."],
  },
  {
    id: "antichess", slug: "antichess", name: "Antichess", rulesId: "antichess",
    engine: { kind: "search" },
    goal: "Lose all your pieces, or have no legal move.",
    reminders: ["If you can capture, you must.", "The king is an ordinary piece: there is no check, and it can be captured.", "Pawns can promote to a king."],
  },
  {
    id: "horde", slug: "horde", name: "Horde", rulesId: "horde",
    engine: { kind: "stockfish", uciVariant: "horde" },
    goal: "Black must capture every white pawn. White wins by checkmate.",
    reminders: ["White has 36 pawns and no king.", "Pawns on the first rank may move two squares.", "Black wins by destroying the whole horde."],
  },
  {
    id: "racingkings", slug: "racing-kings", name: "Racing Kings", rulesId: "racingkings", zone: { squares: FINISH, label: "The finish line" },
    engine: { kind: "stockfish", uciVariant: "racingkings" },
    goal: "Be first to bring your king to the eighth rank.",
    reminders: ["No one may give check, even by accident.", "If White reaches the 8th rank and Black can follow on the next move, it’s a draw."],
  },
];

export function getPlayableVariant(id: string | null | undefined): PlayableVariant | undefined {
  return PLAYABLE_VARIANTS.find((v) => v.id === id || v.slug === id);
}

export type VariantLevel = {
  id: string;
  name: string;
  blurb: string;
  /** Multi-variant Stockfish: MultiPV sampling like the standard bot's human levels, or best play. */
  depth?: number;
  movetime?: number;
  multipv?: number;
  temperature?: number;
  slipRate?: number;
  /** Search bot (Antichess): plies searched. */
  plies: number;
  thinkMs: [number, number];
};

/**
 * Variant bots have no Elo: Stockfish's strength limiter is calibrated for standard chess
 * only, so levels are named, not rated.
 */
export const VARIANT_LEVELS: VariantLevel[] = [
  { id: "novice", name: "Novice", blurb: "Learning the rules too.", depth: 1, multipv: 8, temperature: 260, slipRate: 0.25, plies: 1, thinkMs: [500, 1000] },
  { id: "casual", name: "Casual", blurb: "A friendly game.", depth: 3, multipv: 6, temperature: 120, slipRate: 0.1, plies: 2, thinkMs: [600, 1300] },
  { id: "club", name: "Club", blurb: "Knows the main ideas.", depth: 6, multipv: 5, temperature: 55, slipRate: 0.04, plies: 3, thinkMs: [700, 1500] },
  { id: "strong", name: "Strong", blurb: "Punishes mistakes.", depth: 10, multipv: 4, temperature: 22, slipRate: 0.015, plies: 4, thinkMs: [800, 1600] },
  { id: "maximum", name: "Maximum", blurb: "The engine at full strength.", movetime: 1200, plies: 5, thinkMs: [0, 0] },
];

export function getVariantLevel(id: string | null | undefined): VariantLevel {
  return VARIANT_LEVELS.find((l) => l.id === id) ?? VARIANT_LEVELS[1];
}
